import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';

const PIN_LENGTH = 4;

export default function SetPinPage() {
  const [step, setStep] = useState('create');
  const [pin, setPin] = useState(Array(PIN_LENGTH).fill(''));
  const [confirmPin, setConfirmPin] = useState(Array(PIN_LENGTH).fill(''));
  const [firstPin, setFirstPin] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const { currentUser, refreshProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) navigate('/login', { replace: true });
    inputRefs.current[0]?.focus();
  }, [currentUser, navigate]);

  const handleChange = (index, value, isConfirm = false) => {
    if (!/^\d?$/.test(value)) return;

    if (isConfirm) {
      const newPin = [...confirmPin];
      newPin[index] = value;
      setConfirmPin(newPin);
      if (value && index < PIN_LENGTH - 1) inputRefs.current[index + 1]?.focus();
      if (newPin.every((d) => d !== '') && value) handleSubmit(newPin.join(''));
    } else {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);
      if (value && index < PIN_LENGTH - 1) inputRefs.current[index + 1]?.focus();
      if (newPin.every((d) => d !== '') && value) {
        setFirstPin(newPin.join(''));
        setStep('confirm');
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    const currentArr = isConfirm ? confirmPin : pin;
    if (e.key === 'Backspace' && !currentArr[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (confirmedPin) => {
    if (confirmedPin !== firstPin) {
      toast.error('PINs do not match. Try again.');
      setStep('create');
      setPin(Array(PIN_LENGTH).fill(''));
      setConfirmPin(Array(PIN_LENGTH).fill(''));
      setFirstPin('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        securityPin: confirmedPin,
        updatedAt: serverTimestamp(),
      });

      if (refreshProfile) await refreshProfile();
      toast.success('PIN set successfully!');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Failed to set PIN:', err);
      toast.error('Failed to save PIN. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('create');
    setPin(Array(PIN_LENGTH).fill(''));
    setConfirmPin(Array(PIN_LENGTH).fill(''));
    setFirstPin('');
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  const currentPinArr = step === 'create' ? pin : confirmPin;
  const isConfirm = step === 'confirm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream px-5 py-8 md:px-6 md:py-12">
      <div className="w-full max-w-md text-center">
        <div className="text-5xl mb-6">🔐</div>
        <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-3 tracking-tight">
          {step === 'create' ? 'Set your PIN' : 'Confirm your PIN'}
        </h1>
        <p className="text-body text-base leading-relaxed mb-10">
          {step === 'create'
            ? "Choose a 4-digit security PIN. You'll use this every time you sign in."
            : 'Enter the same PIN again to confirm.'}
        </p>

        <div className="flex gap-3 sm:gap-4 justify-center mb-8">
          {currentPinArr.map((digit, i) => (
            <input
              key={`${step}-${i}`}
              ref={(el) => (inputRefs.current[i] = el)}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value, isConfirm)}
              onKeyDown={(e) => handleKeyDown(i, e, isConfirm)}
              disabled={loading}
              className={`w-12 h-14 sm:w-14 sm:h-16 rounded-2xl border-2 text-2xl sm:text-3xl font-bold text-center text-brand-dark outline-none transition-colors ${
                digit
                  ? 'border-brand-primary bg-brand-primary/[0.04]'
                  : 'border-slate-200 bg-white'
              }`}
            />
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="spinner" />
            <span className="text-body">Saving PIN...</span>
          </div>
        )}

        {step === 'confirm' && (
          <button
            onClick={handleReset}
            className="text-brand-primary font-semibold text-sm bg-transparent border-none cursor-pointer py-2 px-4 mb-6"
          >
            Start over
          </button>
        )}

        <p className="text-muted text-xs leading-relaxed">
          Your PIN adds an extra layer of security to your account. Keep it private and don't share it with anyone.
        </p>
      </div>
    </div>
  );
}